import { Checkbox, Input, Select } from '@/components';
import { useUsers } from '@/features/users/hooks';
import { cn } from '@/lib/cn';
import { useSearchParams } from '@solidjs/router';
import type { DeviceOs, DeviceStatus } from '@uchat-team-management/shared';
import { For, Show, createSignal, onCleanup } from 'solid-js';

export type DeviceFilterParams = {
  q?: string;
  status?: string | string[];
  os?: string;
  assignedUserId?: string;
};

const STATUSES: { value: DeviceStatus; label: string }[] = [
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'lost', label: 'Lost' },
  { value: 'retired', label: 'Retired' },
];

const statusesFrom = (raw: string | string[] | undefined): DeviceStatus[] => {
  const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return STATUSES.map((s) => s.value).filter((s) => values.includes(s));
};

export const DeviceFilters = () => {
  const [params, setParams] = useSearchParams<DeviceFilterParams>();
  const users = useUsers(() => ({}));

  // Debounce search input 200ms before touching the URL (spec).
  const [q, setQ] = createSignal(params.q ?? '');
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  const onSearchInput = (value: string) => {
    setQ(value);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => setParams({ q: value || undefined }), 200);
  };
  onCleanup(() => clearTimeout(debounceTimer));

  const selectedStatuses = () => statusesFrom(params.status);
  const toggleStatus = (status: DeviceStatus) => {
    const current = selectedStatuses();
    const next = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    setParams({ status: next.length > 0 ? next : undefined });
  };

  return (
    <div class="mb-4 flex flex-wrap gap-3">
      <Input
        type="search"
        placeholder="Search name, serial, IMEI"
        value={q()}
        onInput={(e) => onSearchInput(e.currentTarget.value)}
        class="max-w-xs"
      />
      <StatusMultiSelect selected={selectedStatuses()} onToggle={toggleStatus} />
      <Select
        value={params.os ?? ''}
        onChange={(v) => setParams({ os: v || undefined })}
        options={[
          { value: '', label: 'Any OS' },
          { value: 'ios', label: 'iOS' },
          { value: 'android', label: 'Android' },
          { value: 'other', label: 'Other' },
        ]}
        class="min-w-32"
      />
      <Select
        value={params.assignedUserId ?? ''}
        onChange={(v) => setParams({ assignedUserId: v || undefined })}
        options={[
          { value: '', label: 'Any assignment' },
          { value: 'none', label: 'Unassigned' },
          ...(users.data?.data ?? []).map((u) => ({
            value: u.id,
            label: u.displayName || u.email,
          })),
        ]}
        class="min-w-40"
      />
    </div>
  );
};

const StatusMultiSelect = (props: {
  selected: DeviceStatus[];
  onToggle: (status: DeviceStatus) => void;
}) => {
  const [open, setOpen] = createSignal(false);
  let root: HTMLDivElement | undefined;
  const onDocumentClick = (e: MouseEvent) => {
    if (root && !root.contains(e.target as Node)) setOpen(false);
  };
  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  };
  const toggle = () => {
    const next = !open();
    setOpen(next);
    if (next) {
      document.addEventListener('click', onDocumentClick);
      document.addEventListener('keydown', onKeydown);
    }
  };
  onCleanup(() => {
    document.removeEventListener('click', onDocumentClick);
    document.removeEventListener('keydown', onKeydown);
  });

  const label = () =>
    props.selected.length === 0
      ? 'All statuses'
      : STATUSES.filter((s) => props.selected.includes(s.value))
          .map((s) => s.label)
          .join(', ');

  return (
    <div ref={root} class="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open()}
        onClick={toggle}
        class={cn(
          'min-w-36 max-w-56 truncate rounded-lg border border-neutral-300 bg-white px-3 py-2 text-left text-sm dark:border-neutral-700 dark:bg-neutral-900',
          props.selected.length > 0 && 'border-brand-400',
        )}
      >
        {label()}
      </button>
      <Show when={open()}>
        <div
          role="listbox"
          tabIndex={-1}
          class="absolute z-20 mt-1 min-w-40 rounded-lg border border-neutral-200 bg-surface py-1 shadow-lg dark:border-neutral-700 dark:bg-surface-dark"
        >
          <For each={STATUSES}>
            {(s) => (
              <Checkbox
                class="flex w-full px-3 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                checked={props.selected.includes(s.value)}
                onChange={() => props.onToggle(s.value)}
                label={s.label}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};

export const filtersFromParams = (params: DeviceFilterParams) => {
  const statuses = statusesFrom(params.status);
  return {
    q: params.q || undefined,
    status: statuses.length > 0 ? statuses : undefined,
    os:
      params.os && ['ios', 'android', 'other'].includes(params.os)
        ? (params.os as DeviceOs)
        : undefined,
    assignedUserId: params.assignedUserId || undefined,
  };
};
